import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import PiInputfield from "../../components/PiInputField";
import PiSwitch from "../../components/PiSwitch";
import { ActorMovementType } from "../../models/ActorMovementType";
import { ActorState } from "../../models/ActorState";
import { PiSettingsRow } from "../previews/components/PiSettingsList";
import "./ActorStateExploreIndicatorSettings.css";

const DEFAULT_DELAY = 10;

/**
 * Pulls an MUI Switch left so its track (the leftmost visible part) starts on the
 * control column, level with the chips in the rows above and below.
 */
export const SETTINGS_SWITCH_OFFSET = "-4px";

export default function ActorStateExploreIndicatorSettings(props: {
  actorState: ActorState;
  disabled: boolean;
  onChange: (changes: Partial<Pick<ActorState, "exploreShowIndicator" | "exploreIndicatorDelay" | "exploreArrivalDistance">>) => void;
  /**
   * - stack: label above each full-width field (properties column)
   * - rows: two settings-list rows inside a `PiSettingsList` (actor settings popup),
   *   with the hint switch and its delay on one row
   */
  layout?: "stack" | "rows";
}) {
  const { actorState, disabled, onChange, layout = "stack" } = props;
  const { t } = useTranslation();
  const [invalid, setInvalid] = useState(false);
  const [invalidDistance, setInvalidDistance] = useState(false);
  const distance = actorState.exploreArrivalDistance ?? 2;
  const active = useRef(true);
  const showIndicator = actorState.exploreShowIndicator ?? false;
  const delay = actorState.exploreIndicatorDelay ?? DEFAULT_DELAY;
  const isExplore = actorState.actorId === "player" && actorState.movement === ActorMovementType.Explore;

  useEffect(() => {
    active.current = isExplore;
    return () => {
      active.current = false;
    };
  }, [isExplore]);

  useEffect(() => setInvalid(false), [delay]);

  useEffect(() => setInvalidDistance(false), [distance]);

  if (!isExplore) return null;

  const isRows = layout === "rows";

  const onDistanceChange = (text: string) => {
    if (!active.current || disabled || !isExplore) return;
    const value = Number(text);
    const isInvalid = text.trim() === "" || !Number.isFinite(value) || value <= 0;
    setInvalidDistance(isInvalid);
    if (!isInvalid && value !== distance) onChange({ exploreArrivalDistance: value });
  };

  const onShowIndicatorChange = (checked: boolean) => {
    if (!active.current || disabled || !isExplore) return;
    onChange({
      exploreShowIndicator: checked,
      exploreIndicatorDelay: delay,
    });
  };

  const onDelayChange = (text: string) => {
    if (!active.current || disabled || !showIndicator || !isExplore) return;
    const value = Number(text);
    const isInvalid = text.trim() === "" || !Number.isFinite(value) || value < 0;
    setInvalid(isInvalid);
    if (!isInvalid && value !== delay) {
      onChange({ exploreShowIndicator: showIndicator, exploreIndicatorDelay: value });
    }
  };

  const distanceField = (
    <PiInputfield
      id="explore-arrival-distance"
      label={isRows ? "" : String(t("explore_arrival_distance"))}
      ariaLabel={isRows ? String(t("explore_arrival_distance")) : undefined}
      placeholder="2"
      value={String(distance)}
      type="number"
      min={0}
      step="any"
      size="compact"
      showInfoButton={!isRows}
      info={String(t("explore_arrival_distance_tooltip"))}
      disabled={disabled}
      error={invalidDistance}
      errorMessage={String(t("explore_arrival_distance_error"))}
      onChange={onDistanceChange}
    />
  );

  const delayField = (
    <PiInputfield
      id="explore-indicator-delay"
      label={isRows ? "" : String(t("explore_indicator_delay"))}
      ariaLabel={isRows ? String(t("explore_indicator_delay")) : undefined}
      placeholder={String(DEFAULT_DELAY)}
      value={String(delay)}
      type="number"
      min={0}
      step="any"
      size="compact"
      disabled={disabled || !showIndicator}
      error={invalid}
      errorMessage={String(t("explore_indicator_delay_error"))}
      onChange={onDelayChange}
    />
  );

  if (isRows) {
    return (
      <div data-testid="explore-indicator-settings" className="actor-explore-rows contents">
        <PiSettingsRow label={String(t("explore_arrival_distance"))} disabled={disabled}>
          {/* empty switch slot, so this field lines up with the hint delay field below */}
          <span className="actor-explore-switch" aria-hidden="true" />
          <div className="actor-explore-field">{distanceField}</div>
        </PiSettingsRow>
        <PiSettingsRow label={String(t("explore_show_indicator"))} disabled={disabled}>
          <Tooltip title={String(t("explore_show_indicator_tooltip"))} arrow placement="top">
            <span className="actor-explore-switch">
              <Switch
                checked={showIndicator}
                disabled={disabled}
                onChange={(_, checked) => onShowIndicatorChange(checked)}
                inputProps={{ "aria-label": String(t("explore_show_indicator")) }}
                sx={{ ml: SETTINGS_SWITCH_OFFSET }}
              />
            </span>
          </Tooltip>
          <div className="actor-explore-field">{delayField}</div>
        </PiSettingsRow>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[8px]" data-testid="explore-indicator-settings">
      {distanceField}
      <PiSwitch
        checked={showIndicator}
        disabled={disabled}
        label={String(t("explore_show_indicator"))}
        tooltipLocId="explore_show_indicator_tooltip"
        onChange={onShowIndicatorChange}
      />
      {delayField}
    </div>
  );
}
